import { useCallback, useEffect, useMemo, useRef, useState } from "react"

import { TYPING_IDLE_MS } from "./typing-emitter"

interface TypingEvent {
	name?: string
	email?: string
	isTyping?: boolean
}

// Joins names for a typing label: "Jane", "Jane & Mike", "Jane, Mike & Sue".
export function joinTypingNames(names: string[]): string {
	if (names.length <= 1) return names[0] ?? ""
	return `${names.slice(0, -1).join(", ")} & ${names[names.length - 1]}`
}

// Tracks who is currently typing, keyed per user with an independent expiry so
// concurrent typers accumulate into one stable list instead of overwriting each
// other and flashing.
export function useTypingUsers(options?: {
	timeoutMs?: number
	fallbackName?: string
}) {
	// Safety net only: the sender's own stop normally arrives first, so this has
	// to outlive TYPING_IDLE_MS or a slow typist's indicator blinks.
	const timeoutMs = options?.timeoutMs ?? TYPING_IDLE_MS + 2_000
	const fallbackName = options?.fallbackName ?? "Someone"
	const [users, setUsers] = useState<Map<string, string>>(new Map())
	const timers = useRef<Map<string, ReturnType<typeof setTimeout>>>(new Map())

	const remove = useCallback((key: string) => {
		const timer = timers.current.get(key)
		if (timer) clearTimeout(timer)
		timers.current.delete(key)
		setUsers((prev) => {
			if (!prev.has(key)) return prev
			const next = new Map(prev)
			next.delete(key)
			return next
		})
	}, [])

	const handleTyping = useCallback(
		(data: TypingEvent) => {
			const key = data.email || data.name
			if (!key) return
			if (data.isTyping === false) {
				remove(key)
				return
			}
			const name = data.name || data.email || fallbackName
			setUsers((prev) => {
				if (prev.get(key) === name) return prev
				const next = new Map(prev)
				next.set(key, name)
				return next
			})
			const existing = timers.current.get(key)
			if (existing) clearTimeout(existing)
			timers.current.set(
				key,
				setTimeout(() => remove(key), timeoutMs),
			)
		},
		[fallbackName, remove, timeoutMs],
	)

	const clear = useCallback(() => {
		timers.current.forEach(clearTimeout)
		timers.current.clear()
		setUsers(new Map())
	}, [])

	useEffect(() => {
		const active = timers.current
		return () => {
			active.forEach(clearTimeout)
			active.clear()
		}
	}, [])

	const names = useMemo(() => Array.from(users.values()), [users])

	return { names, handleTyping, clear }
}
